import { ShieldAlert, ShieldCheck } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { DashboardCardShell } from './DashboardCardShell';
import type { PartnerEmulationActivityRow } from '@/hooks/useEnterpriseEmulationActivity';

/**
 * M3 (#573): recent enterprise-admin emulation activity (compensating-control
 * surface). Lists admin → target, when it started, and a red badge on
 * security-forced exits. Owns its own skeleton + error + retry via the shell.
 */

interface EmulationActivityListProps {
  emulation: PartnerEmulationActivityRow[];
  isLoading: boolean;
  error: Error | null;
  onRetry?: () => void;
}

function whenLabel(iso: string): string {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

export function EmulationActivityList({
  emulation,
  isLoading,
  error,
  onRetry,
}: EmulationActivityListProps): JSX.Element {
  return (
    <DashboardCardShell
      title="Recent emulation"
      icon={ShieldCheck}
      isLoading={isLoading}
      error={error}
      onRetry={onRetry}
    >
      {emulation.length === 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground">
          No emulation sessions yet.
        </div>
      ) : (
        <ul className="divide-y">
          {emulation.map((e) => (
            <li key={e.id} className="flex items-center justify-between gap-4 py-3">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">
                  {e.admin_email ?? 'Admin'} → {e.target_email ?? 'Member'}
                </div>
                <div className="text-xs text-muted-foreground">
                  {whenLabel(e.started_at)}
                  {e.reason ? ` · ${e.reason}` : ''}
                </div>
              </div>
              {e.forced_by_security && (
                <Badge variant="destructive" className="shrink-0 gap-1">
                  <ShieldAlert className="h-3 w-3" />
                  Security-forced exit
                </Badge>
              )}
            </li>
          ))}
        </ul>
      )}
    </DashboardCardShell>
  );
}
